import { z } from 'zod';
import { ALL_INDUSTRIES, type Industry } from '../types/industry';

/**
 * Zod schema for the user profile API response.
 * Keeps last_model, last_industry, and other fields in sync with the API contract.
 * See: core-api app/schemas/models.py UserProfileResponse
 * Optional/nullable fields use .optional().nullable() so we accept both undefined and null from the API.
 */
export const userProfileResponseSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  company_name: z.string().nullable().optional(),
  position_title: z.string().nullable().optional(),
  email: z.string().email(),
  stripe_customer_id: z.string().nullable().optional(),
  organization_role: z.string().nullable().optional(),
  profile_photo_url: z.string().nullable().optional(),
  has_photo_data: z.boolean().optional(),
  last_model: z.string().nullable().optional(),
  last_industry: z
    .enum(ALL_INDUSTRIES as [Industry, ...Industry[]])
    .nullable()
    .optional(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date().nullable().optional(),
});

export type UserProfileResponse = z.infer<typeof userProfileResponseSchema>;

/** GET /api/v1/users/terms-acceptance — fail closed on wrong shapes (e.g. non-boolean marketing flag). */
export const termsAcceptanceResponseSchema = z.object({
  terms_accepted: z.boolean().default(false),
  terms_accepted_at: z.string().nullable().optional(),
  /** API may send null for “unset”; treat like omitted after parse (see getTermsAcceptance). */
  marketing_emails_opt_in: z.boolean().nullish(),
});

export type TermsAcceptanceResponse = z.infer<typeof termsAcceptanceResponseSchema>;

/** GET /api/v1/users/me/external-api-client — metadata only (no client_secret). */
export const externalApiClientMetadataSchema = z.object({
  id: z.string().min(1),
  user_id: z.string().min(1),
  client_id: z.string().min(1),
  name: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  last_used_at: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  revoked_at: z.string().nullable().optional(),
});

export type ExternalApiClientMetadata = z.infer<typeof externalApiClientMetadataSchema>;

/** POST .../external-api-client/rotate — includes one-time plaintext client_secret. */
export const externalApiClientCreatedSchema = externalApiClientMetadataSchema.extend({
  client_secret: z.string().min(1),
});

export type ExternalApiClientCreated = z.infer<typeof externalApiClientCreatedSchema>;
